import { Process, Product, ProductGroup, ProductGroupType, Vendor, Wiki } from '@/payload-types'
import { CardProps } from '@/ui/components/Card'
import { CardType } from '@/ui/components/Card/types'

/**
 * Computes "Used in" data for a product by matching its processes
 * with the wikis from its productGroup.visibleOn.
 *
 * Uses process.coreStages.stages (regular relationship) instead of
 * wiki.process.docs (join field, not populated on nested docs).
 */
export function computeProductUsedIn(
  product: Product,
  excludeWikiId?: string,
  wikiToProcessMap?: Map<string, Array<{ id: string; title: string }>>,
): { items: Array<{ process: string; wiki: string }>; isAlsoUsedIn: boolean } {
  const productGroup = product?.productGroup?.value as ProductGroup | undefined

  // Build a map of wiki IDs from productGroup.visibleOn
  const wikiMap = new Map<string, Wiki>()
  if (Array.isArray(productGroup?.visibleOn)) {
    for (const ref of productGroup.visibleOn) {
      if (ref.relationTo === 'wiki' && typeof ref.value !== 'string') {
        const wiki = ref.value as Wiki
        wikiMap.set(wiki.id, wiki)
      }
    }
  }
  // Also read product.visibleOn (Solutions have their own visibleOn field)
  const visibleOn = (product as any).visibleOn
  if (Array.isArray(visibleOn)) {
    for (const ref of visibleOn) {
      if (ref.relationTo === 'wiki' && typeof ref.value !== 'string') {
        const wiki = ref.value as Wiki
        wikiMap.set(wiki.id, wiki)
      }
    }
  }

  const usedIn: Array<{ process: string; wiki: string }> = []

  const fullList: Array<{ process: string; wiki: string }> = []

  // Preferred path: use pre-built wikiToProcessMap (reliable, separate API query)
  // Iterate over product's wikiMap (small) and look up in wikiToProcessMap (O(1) per lookup)
  if (wikiToProcessMap) {
    for (const [wikiId, wiki] of wikiMap) {
      const processes = wikiToProcessMap.get(wikiId)
      if (!processes) continue

      for (const process of processes) {
        if (!process.title) continue
        fullList.push({ process: process.title, wiki: wiki.title })
        if (!excludeWikiId || wikiId !== excludeWikiId) {
          usedIn.push({ process: process.title, wiki: wiki.title })
        }
      }
    }
    return { items: usedIn, isAlsoUsedIn: fullList.length > usedIn.length }
  }

  // Fallback: depth-based matching via process.coreStages.stages
  const processes = Array.isArray(product?.process)
    ? product.process.map((p) => p.value as Process).filter(Boolean)
    : []

  for (const process of processes) {
    const stages = Array.isArray(process.coreStages?.stages) ? process.coreStages.stages : []
    const matchingWikiId = stages
      .map((s) => (typeof s === 'string' ? s : s?.id))
      .filter(Boolean)
      .find((stageId) => wikiMap.has(stageId!))

    if (matchingWikiId && process.title) {
      fullList.push({ process: process.title, wiki: wikiMap.get(matchingWikiId)!.title })
      if (!excludeWikiId || matchingWikiId !== excludeWikiId) {
        usedIn.push({ process: process.title, wiki: wikiMap.get(matchingWikiId)!.title })
      }
    }
  }

  return { items: usedIn, isAlsoUsedIn: fullList.length > usedIn.length }
}

/**
 * Maps products to StandardFavorite card props
 * Used by ProductsArchive and ListedProductsSolutions for products
 */
export const mapProductsToCardProps = async (
  products: Product[],
  favourite?: boolean,
  excludeWikiId?: string,
  wikiToProcessMap?: Map<string, Array<{ id: string; title: string }>>,
): Promise<CardProps[]> => {
  return products.map((product) => {
    const vendor = product?.vendor?.value as Vendor
    const vendorLogo = vendor?.general?.image?.resource
    const productGroup = product?.productGroup?.value as ProductGroup
    const productGroupType = productGroup?.category as ProductGroupType
    const processes = Array.isArray(product?.process)
      ? product.process.map((p) => p.value as Process)
      : []
    const { items: usedIn, isAlsoUsedIn } = computeProductUsedIn(
      product,
      excludeWikiId,
      wikiToProcessMap,
    )

    return {
      type: CardType.StandardFavorite,
      appearance: 'outlined',
      title: product.title,
      description: product.previewCardFields?.excerpt,
      href: product?.path ?? '#',
      image: product.previewCardFields?.thumbnail,
      imageTag: productGroupType?.title,
      labels: [
        { title: product?.technologyReadinessLevel ?? '', variant: 'success' },
        ...(Array.isArray(product?.scope)
          ? product.scope.map((scopeItem) => ({
              title: scopeItem,
              variant: 'neutral' as const,
            }))
          : []),
        ...processes.map((process) => ({
          title: process?.title ?? '',
          variant: 'light-blue' as const,
        })),
      ].filter((label) => label.title),
      category:
        product?.productType === 'solution'
          ? { title: 'Solution', colorScheme: '#DBEAFE-#1E40AF' }
          : productGroupType
            ? {
                title: productGroupType.title,
                colorScheme: productGroupType.colorScheme,
              }
            : undefined,
      productGroup: productGroup
        ? { title: productGroup.title, href: productGroup.path ?? '#' }
        : undefined,
      usedIn,
      isAlsoUsedIn,
      company: {
        name: vendor?.title,
        logo: vendorLogo,
      },
      collection: 'products',
      favItemId: product.id,
      initialFavorite: favourite,
    } as CardProps
  })
}

/**
 * Maps solutions (products with productType='solution') to Teaser card props
 * Used by ProductsArchive and ListedProductsSolutions for solutions
 */
export const mapSolutionsToCardProps = async (
  solutions: Product[],
  favourite?: boolean,
  excludeWikiId?: string,
  wikiToProcessMap?: Map<string, Array<{ id: string; title: string }>>,
): Promise<CardProps[]> => {
  return solutions.map((solution) => {
    const vendor = solution?.vendor?.value as Vendor
    const vendorLogo = vendor?.general?.image?.resource
    const productGroup = solution?.productGroup?.value as ProductGroup
    const processes = Array.isArray(solution?.process)
      ? solution.process.map((p) => p.value as Process)
      : []
    const { items: usedIn, isAlsoUsedIn } = computeProductUsedIn(
      solution,
      excludeWikiId,
      wikiToProcessMap,
    )
    return {
      type: CardType.StandardFavorite,
      appearance: 'outlined',
      title: solution.title,
      description: solution.previewCardFields?.excerpt,
      href: solution.path,
      image: solution.previewCardFields?.thumbnail,
      imageTag: 'Solution',
      // A solution is labelled as a solution, never by a product group's category ("Equipment").
      category: { title: 'Solution', colorScheme: '#DBEAFE-#1E40AF' },
      company: {
        name: vendor?.title,
        logo: vendorLogo,
      },
      productGroup: productGroup
        ? { title: productGroup.title, href: productGroup.path ?? '#' }
        : undefined,
      usedIn,
      isAlsoUsedIn,
      labels: [
        ...(Array.isArray(solution?.scope)
          ? solution.scope.map((scopeItem) => ({
              title: scopeItem,
              variant: 'neutral' as const,
            }))
          : []),
        ...processes.map((process) => ({
          title: process?.title ?? '',
          variant: 'light-blue' as const,
        })),
      ].filter((label) => label.title),
      collection: 'products',
      favItemId: solution.id,
      initialFavorite: favourite,
    } as CardProps
  })
}
